import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { storeDefaultInformation } from "../../Store/TemplateStore/template.action";
import {
  editDefaultCIPValues,
  getDefaultCIPValues,
} from "../../network/cipAPI";
import { useToastNotification } from "../../packages/WPToastNotification/WPToastNotificationProvider";
import { useTranslation } from "react-i18next";
import WPTertiaryButton from "../../packages/WPTertiaryButton";
import { SaveIcon } from "../../packages/SVGs";
import CircularLoader from "../../common/styles/components/circularLoader";
import IconWithTooltip from "../../common/styles/components/headings/IconWithTooltip";
import CIPSection from "./CIPSection";
import { INITIAL_DEFAULT_CIP_ERROR_STATE } from "./defaultCIPInformationFields.config";
import {
  STAGE_DEFAULTS,
  DEFAULT_CIP_AMOUNT_PER_ELEMENT,
} from "../../constants/global.constants";
import { getLocalStorageItem } from "../../utils/session";
import { isNullOrUndefined } from "../../utils/appUtils";

import "./index.scss";

const DefaultCIPInformation = React.forwardRef(
  ({
    selectedTrain,
    defaultCIPKeys,
    disabled,
    setMandatoryComponentsStatus,
    noOfRONFElements,
    selectedPass,
    selectedStage,
  }) => {
    const { showSuccessNotification, showErrorNotification } =
      useToastNotification();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { temperature, cips } = useSelector((state) => state);
    const [changedCIPValues, setChangedCIPValues] = useState({});
    const [errors, setErrors] = useState(INITIAL_DEFAULT_CIP_ERROR_STATE);
    const [noError, setNoError] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    const defaultCIPState = useSelector((state) => ({
      acid_used_per_cip: state.acid_used_per_cip,
      alkali_used_per_cip: state.alkali_used_per_cip,
      additional_chemical_per_cip: state.additional_chemical_per_cip,
      acid_cip_ph: state.acid_cip_ph,
      alkali_cip_ph: state.alkali_cip_ph,
      cip_solution_ph: state.cip_solution_ph,
      acid_cip_temp: state.acid_cip_temp,
      alkali_cip_temp: state.alkali_cip_temp,
      cip_solution_temp: state.cip_solution_temp,
      acid_cip_duration: state.acid_cip_duration,
      alkali_cip_duration: state.alkali_cip_duration,
      cip_solution_duration: state.cip_solution_duration,
    }));

    const handleInputChange = (field, value, minValue, maxValue) => {
      setChangedCIPValues((prev) => ({ ...prev, [field]: value }));

      const isFieldHasError =
        value === "" ||
        parseFloat(value) < minValue ||
        parseFloat(value) > maxValue;

      setErrors((prev) => ({
        ...prev,
        [field]: isFieldHasError ? true : null,
      }));
    };

    // Helper to fetch and restore last saved values
    const fetchAndRestoreDefaultCIP = async () => {
      try {
        const res = await getDefaultCIPValues(selectedTrain);
        const defaultInfo = res?.data;
        if (defaultInfo) {
          dispatch(storeDefaultInformation(defaultInfo));
        }
      } catch (getError) {
        showErrorNotification(t("defaultInformationStrings.fetchFailed"));
      }
    };

    const checkChangedCIPValues = () =>
      Object.keys(changedCIPValues).length > 0;

    const getSaveButtonState = () => {
      let disabledState = true;
      const hasChanges = checkChangedCIPValues();

      if (hasChanges && noError) {
        disabledState = false;
      }

      return disabledState;
    };

    const handleSave = async (newChangedValues = {}) => {
      const payload = { ...changedCIPValues, ...newChangedValues };
      setIsSaving(true);
      try {
        const editRes = await editDefaultCIPValues(selectedTrain, payload);
        if (editRes?.detail || Object.keys(editRes).length === 0) {
          showErrorNotification(t("defaultInformationStrings.saveFailed"));
          await fetchAndRestoreDefaultCIP();
          return;
        }
        // Only update store if save is successful
        dispatch(storeDefaultInformation(editRes));

        // don't show success notification in case auto-save
        if (Object.keys(newChangedValues).length === 0) {
          showSuccessNotification(t("defaultInformationStrings.saveSuccess"));
        }
      } catch (editError) {
        showErrorNotification(t("defaultInformationStrings.saveFailed"));
        await fetchAndRestoreDefaultCIP();
      } finally {
        setChangedCIPValues({});
        setIsSaving(false);
      }
    };

    useEffect(() => {
      const noErrorVal = Object.values(errors).every((error) => error === null);
      setNoError(noErrorVal);
    }, [errors]);

    useEffect(() => {
      if (cips === null) {
        setErrors((prev) => ({
          ...prev,
          acid_cip_temp: null,
          alkali_cip_temp: null,
          cip_solution_temp: null,
        }));
      }
    }, [temperature, cips]);

    useEffect(() => {
      setChangedCIPValues({});
      setErrors(INITIAL_DEFAULT_CIP_ERROR_STATE);
    }, [selectedTrain]);

    useEffect(() => {
      if (setMandatoryComponentsStatus == null) return;
      const hasChanges = checkChangedCIPValues();
      const defaultCIPInfoState = {
        fulfilled: !hasChanges && noError,
        changed: hasChanges,
        error: !noError,
      };
      setMandatoryComponentsStatus((prev) => {
        return { ...prev, defaultCIPInfo: defaultCIPInfoState };
      });
    }, [changedCIPValues, noError]);

    const initialLoadRef = useRef(true);
    const prevNoOfRONFElementsRef = useRef(noOfRONFElements);

    useEffect(() => {
      if (isNullOrUndefined(noOfRONFElements)) return;
      if (initialLoadRef.current) {
        initialLoadRef.current = false;

        const prevTrainConfig = JSON.parse(
          getLocalStorageItem("prevTrainConfig"),
        );

        if (!prevTrainConfig) {
          const { NUM_PRESSURE_VESSELS, NUM_ELEMENTS_PER_PV } = STAGE_DEFAULTS;
          prevNoOfRONFElementsRef.current =
            NUM_PRESSURE_VESSELS * NUM_ELEMENTS_PER_PV;
        } else {
          if (selectedStage === "All") {
            let totalElements = 0;

            Object.values(
              prevTrainConfig[selectedTrain]?.[selectedPass],
            ).forEach((stageElements) => {
              totalElements += stageElements;
            });
            prevNoOfRONFElementsRef.current = totalElements;
          } else {
            prevNoOfRONFElementsRef.current =
              prevTrainConfig[selectedTrain]?.[selectedPass]?.[selectedStage];
          }
        }
      }

      const { NUM_PRESSURE_VESSELS, NUM_ELEMENTS_PER_PV } = STAGE_DEFAULTS;
      const { ACID, ALKALI } = DEFAULT_CIP_AMOUNT_PER_ELEMENT;
      const defaultNoOfElements = NUM_PRESSURE_VESSELS * NUM_ELEMENTS_PER_PV;

      const prevNoOfElements =
        Number(prevNoOfRONFElementsRef.current) || defaultNoOfElements;
      const currNoOfElements = Number(noOfRONFElements) || defaultNoOfElements;

      const currAcid = (ACID * currNoOfElements).toFixed(2);
      const currAlkali = (ALKALI * currNoOfElements).toFixed(2);

      // Always update acid and alkali values when number of elements changes
      const newChangedCIPValues = {
        acid_used_per_cip: currAcid,
        alkali_used_per_cip: currAlkali,
      };
      handleSave(newChangedCIPValues);
      prevNoOfRONFElementsRef.current = noOfRONFElements;
    }, [noOfRONFElements]);

    return (
      <div className="ro-card-wrapper position-relative">
        {isSaving && (
          <div className="circular-loader-overlay">
            <CircularLoader />
          </div>
        )}
        <div>
          <div className="ro-card-header">
            <div className=" info-section">
              <div className="teal-heading">
                {t("defaultInformationStrings.defaultInformation")}
              </div>
              {!disabled && (
                <IconWithTooltip
                  label={t("helpInformation.defaultCIPInformationHelp")}
                />
              )}
            </div>
            <WPTertiaryButton
              variant="outlined"
              label={t("defaultInformationStrings.save")}
              onClick={handleSave}
              icon={<SaveIcon width="16" height="16" />}
              iconPlacement="before"
              size="small"
              disabled={getSaveButtonState() || disabled}
            />
          </div>
          <CIPSection
            defaultCIPKeys={defaultCIPKeys}
            handleInputChange={handleInputChange}
            disabled={disabled}
            existingCIPState={changedCIPValues}
          />
        </div>
      </div>
    );
  },
);

export default DefaultCIPInformation;
